import SentryLogger from './SentryLogger';
import ConsoleLogger from './ConsoleLogger';
import { sessionID } from '../../api_services/SessionID';

const config = LogConfig;

export interface ILogger {
  log(message: string, payload?: object): void;

  info(message: string, payload?: object): void;

  warn(message: string, payload?: object): void;

  error(message: string, payload?: object): void;

  exception(exception: Error, payload?: object): void;

  setUserContext({ email }: { email: string }): void;

  setExtraContext(contextData: object): void;

  captureBreadcrumb(message: string, data?: object): void;
}

class LoggersFacade implements ILogger {
  loggers: ILogger[];
  sessionId: string;

  constructor() {
    this.sessionId = sessionID;
    this.loggers = [ConsoleLogger.create()];

    if (config.sentry.enabled) {
      this.loggers.push(
        SentryLogger.create({
          ...config.sentry,
          release: `pilot-app@${Version}`,
          sessionId: this.sessionId,
          environment: Environment,
        }),
      );
    }
  }

  _eachLoggerCall(method: string, args: unknown[]) {
    this.loggers.forEach((logger) => {
      try {
        logger[method](...args);
      } catch (err) {
        console.error(`Failed to call "${method}" on "${logger}"`);
      }
    });
  }

  log(message: string, payload?: object) {
    this._eachLoggerCall('log', [message, payload]);
  }

  info(message: string, payload?: object) {
    this._eachLoggerCall('info', [message, payload]);
  }

  warn(message: string, payload?: object) {
    this._eachLoggerCall('warn', [message, payload]);
  }

  error(message: string | Error, payload?: object) {
    const messageText = message instanceof Error ? message.message : message;

    this._eachLoggerCall('error', [messageText, payload]);
  }

  exception(exception: Error, payload?: object) {
    this._eachLoggerCall('exception', [exception, payload]);
  }

  setUserContext({ email }: { email: string }) {
    this._eachLoggerCall('setUserContext', [{ email }]);
  }

  setExtraContext(contextData: object) {
    this._eachLoggerCall('setExtraContext', [contextData]);
  }

  captureBreadcrumb(message: string, data: object) {
    this._eachLoggerCall('captureBreadcrumb', [message, data]);
  }
}

const loggerInstance = new LoggersFacade();

export default loggerInstance;
